import { Controller } from "@hotwired/stimulus"

// A persisted server attempt must precede any external dial action. A replay is
// only a receipt: reopening the destination would place a second call.
export default class extends Controller {
  static targets = ["start", "cancel", "status", "attempt", "attemptVersion", "leadVersion", "save", "handoff"]
  static values = { startUrl: String, cancelUrl: String, reservationVersion: String, handoffKinds: Array }

  connect() {
    // Stimulus can reconnect this instance while an older request is pending.
    this.connection = {}
    this.pending = false
  }

  disconnect() {
    this.connection = null
    this.pendingPopup?.close()
    this.pendingPopup = null
  }

  isCurrentConnection(connection) {
    return connection && connection === this.connection && this.element.isConnected
  }

  revealHandoff(event) {
    if (this.hasHandoffTarget && this.handoffKindsValue.includes(event.currentTarget.value)) {
      this.handoffTarget.open = true
    }
  }

  async start(event) {
    event.preventDefault()
    const connection = this.connection
    if (!this.isCurrentConnection(connection) || this.pending || this.attemptTarget.value) return
    const voice = event.currentTarget.dataset.mode === "voice"
    const popup = voice ? window.open("about:blank", "_blank") : null
    if (voice && !popup) {
      this.statusTarget.textContent = "Allow popups, then try again. No call was started."
      return
    }
    if (popup) popup.opener = null
    this.pendingPopup = popup
    this.pending = true
    this.startTargets.forEach(button => { button.disabled = true })
    this.requestKey ||= crypto.randomUUID()
    try {
      const data = await this.post(this.startUrlValue, {
        request_key: this.requestKey,
        expected_lock_version: this.reservationVersionValue || null
      })
      if (!this.isCurrentConnection(connection)) {
        popup?.close()
        return
      }
      if (data.attempt_id && data.attempt_status === "started") {
        this.attemptTarget.value = data.attempt_id
        this.attemptVersionTarget.value = data.attempt_version
        if (this.hasLeadVersionTarget && this.leadVersionTarget.value === "" && data.lead_version != null) {
          this.leadVersionTarget.value = String(data.lead_version)
        }
        this.saveTargets.forEach(button => { button.disabled = false })
        this.cancelTarget.hidden = false
      }
      if (!data.launchable || !/^\+1\d{10}$/.test(data.destination || "")) {
        popup?.close()
        this.statusTarget.textContent = "This start was already recorded. No new call was launched. Reload to review its status."
        return
      }
      this.statusTarget.textContent = "Call started. Record its outcome, or confirm that the call did not start."
      this.pendingPopup = null
      if (voice) popup.location.replace(`https://voice.google.com/u/0/calls?a=nc,${encodeURIComponent(data.destination)}`)
      else window.location.assign(`tel:${data.destination}`)
    } catch (error) {
      popup?.close()
      if (!this.isCurrentConnection(connection)) return
      this.statusTarget.textContent = `${error.message} Reload to review contact status before trying again.`
    } finally {
      if (this.isCurrentConnection(connection)) {
        this.pending = false
        this.pendingPopup = null
      }
    }
  }

  async cancel(event) {
    event.preventDefault()
    const connection = this.connection
    if (!this.isCurrentConnection(connection) || this.pending || !this.attemptTarget.value) return
    this.pending = true
    this.cancelTarget.disabled = true
    this.cancelKey ||= crypto.randomUUID()
    try {
      await this.post(this.cancelUrlValue, {
        attempt_id: this.attemptTarget.value,
        expected_lock_version: this.attemptVersionTarget.value,
        request_key: this.cancelKey
      })
      if (!this.isCurrentConnection(connection)) return
      this.attemptTarget.value = ""
      this.attemptVersionTarget.value = ""
      this.saveTargets.forEach(button => { button.disabled = true })
      this.cancelTarget.hidden = true
      this.statusTarget.textContent = "Cancelled without recording a conversation. Reload to check availability before starting again."
    } catch (error) {
      if (!this.isCurrentConnection(connection)) return
      this.statusTarget.textContent = `${error.message} Reload to review the attempt.`
    } finally {
      if (this.isCurrentConnection(connection)) {
        this.pending = false
        this.cancelTarget.disabled = false
      }
    }
  }

  async post(url, payload) {
    const response = await fetch(url, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify(payload)
    })
    const data = await response.json()
    if (!response.ok) throw new Error(`Contact unavailable: ${String(data.reason || "request failed").replaceAll("_", " ")}.`)
    return data
  }
}
